import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Database, Search, Filter, BarChart, Package,
  DollarSign, Truck, Warehouse, ShoppingCart,
  Users, FileText, Plus, ChevronRight, Calendar
} from 'lucide-react';

const translations = {
  es: {
    title: 'Sistema ERP',
    subtitle: 'Planificación de Recursos Empresariales',
    sections: {
      inventory: 'Inventario',
      orders: 'Pedidos',
      purchases: 'Compras',
      accounting: 'Contabilidad',
      hr: 'Recursos Humanos'
    },
    actions: {
      newOrder: 'Nuevo pedido',
      newPurchase: 'Nueva compra',
      newProduct: 'Nuevo producto'
    },
    search: 'Buscar...',
    filters: 'Filtros',
    status: {
      active: 'Activo',
      pending: 'Pendiente',
      completed: 'Completado'
    }
  },
  en: {
    title: 'ERP System',
    subtitle: 'Enterprise Resource Planning',
    sections: {
      inventory: 'Inventory',
      orders: 'Orders',
      purchases: 'Purchases',
      accounting: 'Accounting',
      hr: 'Human Resources'
    },
    actions: {
      newOrder: 'New Order',
      newPurchase: 'New Purchase',
      newProduct: 'New Product'
    },
    search: 'Search...',
    filters: 'Filters',
    status: {
      active: 'Active',
      pending: 'Pending',
      completed: 'Completed'
    }
  }
};

// Mock data
const mockInventory = [
  {
    id: 1,
    name: 'Laptop Pro X1',
    sku: 'LPX1-2025',
    quantity: 45,
    reorderPoint: 20,
    unitPrice: 999.99,
    location: 'Warehouse A'
  },
  {
    id: 2,
    name: 'Office Chair Ergo',
    sku: 'OCE-2025',
    quantity: 78,
    reorderPoint: 15,
    unitPrice: 299.99,
    location: 'Warehouse B'
  }
];

const mockOrders = [
  {
    id: 1,
    customer: 'Tech Solutions Inc',
    date: '2025-03-14',
    total: 4999.95,
    status: 'pending',
    items: 5
  },
  {
    id: 2,
    customer: 'Office Supplies Co',
    date: '2025-03-13',
    total: 1499.95,
    status: 'completed',
    items: 3
  }
];

const ERPPage = () => {
  const [activeSection, setActiveSection] = useState('inventory');
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
            <p className="text-foreground/70">{t.subtitle}</p>
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
              <Plus className="h-4 w-4 mr-2" />
              {activeSection === 'inventory' ? t.actions.newProduct :
               activeSection === 'orders' ? t.actions.newOrder :
               t.actions.newPurchase}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t.search}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <Filter className="h-4 w-4 mr-2" />
              {t.filters}
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <BarChart className="h-4 w-4 mr-2" />
              {t.sections.reports}
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                }`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Section */}
        {activeSection === 'inventory' && (
          <div className="space-y-6">
            {mockInventory.map((item) => (
              <div
                key={item.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Package className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{item.name}</h3>
                      <p className="text-foreground/70">SKU: {item.sku}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button className="text-foreground/70 hover:text-foreground">
                      <Warehouse className="h-5 w-5" />
                    </button>
                    <button className="text-foreground/70 hover:text-foreground">
                      <ShoppingCart className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Quantity</p>
                    <p className="text-sm font-medium text-foreground">{item.quantity}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Reorder Point</p>
                    <p className="text-sm font-medium text-foreground">{item.reorderPoint}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Unit Price</p>
                    <p className="text-sm font-medium text-foreground">${item.unitPrice}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Location</p>
                    <p className="text-sm font-medium text-foreground">{item.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'orders' && (
          <div className="space-y-6">
            {mockOrders.map((order) => (
              <div
                key={order.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{order.customer}</h3>
                      <p className="text-foreground/70">Order #{order.id}</p>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Date</p>
                    <p className="text-sm font-medium text-foreground">{order.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Items</p>
                    <p className="text-sm font-medium text-foreground">{order.items}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Total</p>
                    <p className="text-sm font-medium text-foreground">${order.total}</p>
                  </div>
                  <div className="flex justify-end">
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      View Details
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ERPPage;